/**
 * ChapterMap — mappa disegnata di un capitolo (TASK-020): nodi = concetti,
 * archi a riposo in tre tratti, fisica ibrida di ritorno, fuoco con archi
 * colorati + verbi + esterni in cerchio, filtro per famiglia, ripasso.
 *
 * Componente DOM puro (nessuna dipendenza Inertia/router): il chiamante
 * (web `apps/platform`, desktop `apps/desktop`) possiede navigazione e
 * fetch. Stili in `concept-map.css` (solo variabili DS, nessuna classe
 * Tailwind) così il componente non dipende dai content globs del
 * consumatore (vedi Phase 0 del task — il desktop non ha Tailwind).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { Simulation } from 'd3-force'
import {
  computeFocus,
  createMapSimulation,
  crossBadgeCounts,
  DEFAULT_PHYSICS,
  edgesForFamily,
  estimateNodeSize,
  fitLayout,
  layoutChapter,
  placeRing,
  relationFamily,
  relationVerb,
  updateMapSimulation,
  type ChapterGraph,
  type MapLocale,
  type RelationFamily,
  type SimNode,
} from '@arianna/reader-core'
import { FAMILY_LABEL } from './familyLabel.js'
import './concept-map.css'

export interface ChapterMapProps {
  graph: ChapterGraph
  chapterLabel: string
  focusNodeId: string | null
  family: RelationFamily | 'all'
  recall: boolean
  locale: MapLocale
  onFocusChange(nodeId: string | null): void
  onFamilyChange(family: RelationFamily | 'all'): void
  onRecallChange(recall: boolean): void
  onOpenPanel(nodeId: string): void
  onGoToManual(blockId: string): void
  onGoToExternal(target: { chapterId: string; nodeId: string }): void
  onSpeak?(text: string): void
  readOnly?: boolean
  /** chapterId -> titolo, per etichettare gli esterni. Assente -> "Cap. <chapterId>". */
  chapterLabels?: Record<string, string>
}

const FAMILIES: RelationFamily[] = ['structure', 'dynamic', 'contrast']

const COPY = {
  it: {
    recall: 'Ripasso',
    allRelations: 'Tutte le relazioni',
    listen: 'Ascolta',
    goToManual: 'Vai al manuale',
    backHint: 'Tocca lo sfondo per tornare alla mappa',
  },
  en: {
    recall: 'Recall',
    allRelations: 'All relations',
    listen: 'Listen',
    goToManual: 'Go to manual',
    backHint: 'Tap the background to go back to the map',
  },
}

const DRAG_THRESHOLD = 4
/** Durata della dissolvenza degli esterni in uscita dal fuoco (ms). */
const LEAVE_MS = 450

function externalLabel(chapterId: string, chapterLabels?: Record<string, string>): string {
  return chapterLabels?.[chapterId] ?? `Cap. ${chapterId}`
}

export function ChapterMap({
  graph,
  chapterLabel,
  focusNodeId,
  family,
  recall,
  locale,
  onFocusChange,
  onFamilyChange,
  onRecallChange,
  onOpenPanel,
  onGoToManual,
  onGoToExternal,
  onSpeak,
  readOnly = false,
  chapterLabels,
}: ChapterMapProps) {
  const copy = COPY[locale]
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [viewport, setViewport] = useState({ width: 640, height: 480 })
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [userScale, setUserScale] = useState<number | null>(null)

  const layout = useMemo(() => layoutChapter(graph), [graph])
  const badges = useMemo(() => crossBadgeCounts(graph), [graph])
  const focus = useMemo(
    () =>
      focusNodeId != null && graph.nodes.some((n) => n.nodeId === focusNodeId)
        ? computeFocus(graph, focusNodeId)
        : null,
    [graph, focusNodeId],
  )
  const filteredEdgeIds = useMemo(() => edgesForFamily(graph, family), [graph, family])

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

  // Simulazione: nodi residenti sempre presenti; esterni entrano/escono col fuoco.
  // Un solo effetto la crea/ricrea quando il layout cambia (nuovo capitolo),
  // partendo dalle posizioni "a casa" — evita un secondo effetto che si
  // limiti a resettare lo stato in cascata.
  const simRef = useRef<Simulation<SimNode, undefined> | null>(null)
  const [simNodes, setSimNodes] = useState<Record<string, SimNode>>(() =>
    initSimNodes(layout, graph),
  )
  // Specchio dell'ultimo simNodes pubblicato (tick + set espliciti), letto
  // dall'effetto di ingresso/uscita dal fuoco per calcolare il prossimo stato
  // FUORI dall'updater di setSimNodes (StrictMode lo eseguirebbe due volte,
  // chiamando updateMapSimulation due volte con effetti collaterali su `sim`).
  const simNodesRef = useRef(simNodes)
  useEffect(() => {
    simNodesRef.current = simNodes
  }, [simNodes])
  const prevFocusRef = useRef<string | null>(null)
  // Pan/zoom animati (CSS) quando cambiano per un fuoco o un fit; spenti
  // durante drag, pinch e rotella, dove devono seguire la mano.
  const [viewAnimated, setViewAnimated] = useState(false)

  const fit = useMemo(
    () =>
      fitLayout(layout, {
        width: viewport.width,
        height: viewport.height,
        padding: 24,
        minScale: 0.6,
      }),
    [layout, viewport],
  )
  const scale = userScale ?? fit.scale
  // Specchio di fit/scale/viewport letto dai gestori di pan/zoom.
  const viewRef = useRef({ ...fit, scale, width: viewport.width, height: viewport.height })
  useEffect(() => {
    viewRef.current = { ...fit, scale, width: viewport.width, height: viewport.height }
  }, [fit, scale, viewport])

  // A fuoco: zoom automatico perché l'anello entri nel viewport (clamp 0.6–1.5)
  // e centratura sul nodo; ricalcolati se cambiano viewport o fit. Senza fuoco
  // la mappa torna al fit. L'utente può poi zoomare/spostare a mano.
  const [focusRing, setFocusRing] = useState<{
    cx: number
    cy: number
    rx: number
    ry: number
  } | null>(null)
  useEffect(() => {
    if (focusRing == null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUserScale(null)
      setPan({ x: 0, y: 0 })
      return
    }
    const sx = focusRing.rx > 0 ? (viewport.width / 2 - 32) / focusRing.rx : fit.scale
    const sy = focusRing.ry > 0 ? (viewport.height / 2 - 72) / focusRing.ry : fit.scale
    const s = Math.max(0.6, Math.min(1.5, Math.min(fit.scale, sx, sy)))
    setUserScale(s)
    setPan({
      x: viewport.width / 2 - (fit.offsetX + focusRing.cx * s),
      y: viewport.height / 2 - (fit.offsetY + focusRing.cy * s),
    })
  }, [focusRing, fit, viewport])

  useEffect(() => {
    const physics = reducedMotion ? { ...DEFAULT_PHYSICS, alphaDecay: 0.2 } : DEFAULT_PHYSICS
    const initialNodes = initSimNodes(layout, graph)
    const sim = createMapSimulation(Object.values(initialNodes), [], physics)
    sim.on('tick', () => {
      const next: Record<string, SimNode> = {}
      for (const n of sim.nodes()) next[n.id] = n
      setSimNodes(next)
    })
    simRef.current = sim
    // Sincronizza lo stato React con la simulazione d3-force esterna (nuovo capitolo).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSimNodes(initialNodes)
    // Nuovo layout -> il pan utente precedente non ha più senso (è relativo
    // al layout vecchio): riparte da {0,0}, l'offset di centratura resta in
    // `fit.offsetX/offsetY` applicato separatamente nel transform.
    setPan({ x: 0, y: 0 })
    return () => {
      sim.stop()
      simRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, graph])

  // Ingresso/uscita dal fuoco (raggiera): a fuoco la simulazione contiene SOLO
  // il fuoco + i vicini (residenti e esterni) sull'anello; i residenti non
  // collegati non si disegnano. All'uscita tutti i residenti tornano alle case
  // del layout a strati (posizione corrente = casa, niente salti) e gli esterni
  // spariscono. Le case dei residenti nascosti vivono nel layout: si ricalcolano
  // da `initSimNodes` (equivalente a ricordarle in un ref).
  useEffect(() => {
    const sim = simRef.current
    if (sim == null) return

    const homes = initSimNodes(layout, graph)
    const current: Record<string, SimNode> = {}
    for (const n of sim.nodes()) current[n.id] = n
    const next: Record<string, SimNode> = {}
    const extLinks: Array<{ source: string; target: string }> = []

    if (focus == null) {
      // Uscita dal fuoco: i residenti restano dove sono e TORNANO a casa con
      // la fisica (niente salti); gli esterni rientrano nel nodo che era a
      // fuoco e svaniscono, poi vengono rimossi (timer sotto).
      for (const [id, home] of Object.entries(homes)) {
        const prev = current[id]
        next[id] = { ...home, x: prev?.x ?? home.x, y: prev?.y ?? home.y }
      }
      const prevFocus = prevFocusRef.current != null ? current[prevFocusRef.current] : undefined
      for (const n of Object.values(current)) {
        if (n.ext !== true) continue
        const target = prevFocus ?? n
        next[n.id] = { ...n, hx: target.x, hy: target.y, leaving: true }
      }
    } else {
      const focusHome = homes[focus.focusNodeId]
      const focusCur = current[focus.focusNodeId] ?? focusHome
      if (focusCur == null) return
      const center = { x: focusCur.x, y: focusCur.y }
      next[focus.focusNodeId] = {
        ...(focusHome ?? focusCur),
        x: center.x,
        y: center.y,
        hx: center.x,
        hy: center.y,
      }
      // Ordine sull'anello: i residenti per l'angolo in cui si trovano GIÀ
      // rispetto al fuoco (si muovono poco, niente incroci), gli esterni
      // inseriti negli spazi tra loro in modo proporzionale.
      const residents = focus.neighbors
        .filter((nb) => !nb.external)
        .map((nb) => {
          const cur = current[nb.nodeId] ?? homes[nb.nodeId]
          const ang = cur != null ? Math.atan2(cur.y - center.y, cur.x - center.x) : 0
          return { nb, ang }
        })
        .sort((p, q) => p.ang - q.ang)
      const externals = focus.neighbors.filter((nb) => nb.external)
      const ordered: typeof focus.neighbors = []
      const total = residents.length + externals.length
      let ei = 0
      for (let i = 0; i < total; i++) {
        // Bresenham: esterni distribuiti tra i residenti in proporzione.
        const wantExt =
          externals.length > 0 &&
          ei < externals.length &&
          Math.floor(((i + 1) * externals.length) / total) > ei
        if (wantExt) ordered.push(externals[ei++]!)
        else if (residents.length > 0)
          ordered.push(residents[ordered.length - ei]?.nb ?? externals[ei++]!)
        else ordered.push(externals[ei++]!)
      }
      const startAngle = residents[0]?.ang ?? -Math.PI / 2
      const ring = placeRing(center, ordered.length, DEFAULT_PHYSICS, startAngle)
      ordered.forEach((nb, i) => {
        const pos = ring[i] ?? center
        if (nb.external) {
          const ext = graph.externalNodes.find((e) => e.nodeId === nb.nodeId)
          const size = estimateNodeSize(ext?.label ?? nb.nodeId, true)
          const prev = current[nb.nodeId]
          next[nb.nodeId] = {
            id: nb.nodeId,
            // Nasce dal nodo a fuoco (un passo verso il suo posto, così la
            // direzione è definita) e raggiunge l'anello con la fisica.
            x: prev?.x ?? center.x + (pos.x - center.x) * 0.15,
            y: prev?.y ?? center.y + (pos.y - center.y) * 0.15,
            hx: pos.x,
            hy: pos.y,
            w: size.w,
            h: size.h,
            r: Math.max(size.w, size.h) / 2 + 6,
            ext: true,
          }
        } else {
          const home = homes[nb.nodeId]
          if (home == null) return
          const prev = current[nb.nodeId]
          next[nb.nodeId] = {
            ...home,
            x: prev?.x ?? home.x,
            y: prev?.y ?? home.y,
            hx: pos.x,
            hy: pos.y,
          }
        }
        extLinks.push({ source: focus.focusNodeId, target: nb.nodeId })
      })
      // I residenti non collegati restano in scena, sfumati, fermi alle loro
      // case del layout a strati: niente stacchi tra riposo e fuoco.
      for (const [id, home] of Object.entries(homes)) {
        if (next[id] != null) continue
        const prev = current[id]
        next[id] = { ...home, x: prev?.x ?? home.x, y: prev?.y ?? home.y, bg: true }
      }
    }

    updateMapSimulation(sim, Object.values(next), extLinks, { focus: focus != null })
    // Sincronizza lo stato React con la simulazione d3-force esterna (ingresso/uscita dal fuoco).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSimNodes(next)
    setViewAnimated(true)
    prevFocusRef.current = focus?.focusNodeId ?? null
    // Gli esterni in uscita vengono tolti dopo la dissolvenza.
    let leaveTimer: ReturnType<typeof setTimeout> | null = null
    if (Object.values(next).some((n) => n.leaving === true)) {
      leaveTimer = setTimeout(() => {
        const s = simRef.current
        if (s == null) return
        const kept: Record<string, SimNode> = {}
        for (const n of s.nodes()) if (n.leaving !== true) kept[n.id] = n
        updateMapSimulation(s, Object.values(kept), [])
        setSimNodes(kept)
      }, LEAVE_MS)
    }
    // A fuoco la vista si centra sul nodo scelto; senza fuoco torna al fit.
    // Geometria dell'anello (sulle mete hx/hy, non sulle posizioni correnti):
    // l'effetto sotto la usa per zoom e centratura, seguendo viewport e fit.
    const focusSim = focus != null ? next[focus.focusNodeId] : undefined
    if (focusSim != null) {
      let rx = 0
      let ry = 0
      for (const n of Object.values(next)) {
        rx = Math.max(rx, Math.abs(n.hx - focusSim.hx) + n.w / 2)
        ry = Math.max(ry, Math.abs(n.hy - focusSim.hy) + n.h / 2)
      }
      setFocusRing({ cx: focusSim.hx, cy: focusSim.hy, rx, ry })
    } else {
      setFocusRing(null)
    }
    return () => {
      if (leaveTimer != null) clearTimeout(leaveTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNodeId, layout, graph])

  // ResizeObserver + fitLayout. Layout effect: la misura iniziale avviene prima
  // del primo paint, senza un frame col fit sbagliato.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (el == null) return
    // Misura subito al montaggio: la prima notifica del ResizeObserver può
    // arrivare in ritardo o mancare, e il fit resterebbe su 640×480.
    const rect = el.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      setViewport({ width: rect.width, height: rect.height })
    }
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry != null) {
        setViewport({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const dragState = useRef<{
    nodeId: string
    startX: number
    startY: number
    startNodeX: number
    startNodeY: number
    moved: boolean
  } | null>(null)

  // readOnly (desktop) toglie SOLO "Vai al manuale" (contratto § Componente DOM):
  // drag e tap-to-focus restano attivi anche nella preview dell'autore.
  // dragState serve a distinguere tap da drag in nodePointerUp via `drag.moved`.
  function nodePointerDown(e: ReactPointerEvent<HTMLDivElement>, nodeId: string) {
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* alcuni ambienti di test non implementano setPointerCapture */
    }
    const node = simNodes[nodeId]
    dragState.current = {
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      // Posizione CORRENTE del nodo (non la casa hx/hy): un nodo già
      // spostato dalla fisica deve continuare a muoversi da lì, non
      // saltare alla sua posizione di riposo.
      startNodeX: node?.x ?? 0,
      startNodeY: node?.y ?? 0,
      moved: false,
    }
    const sim = simRef.current
    if (sim != null && !reducedMotion) sim.alphaTarget(0.3).restart()
  }

  function nodePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragState.current
    if (drag == null) return
    const dx = (e.clientX - drag.startX) / scale
    const dy = (e.clientY - drag.startY) / scale
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) drag.moved = true
    const node = simNodes[drag.nodeId]
    if (node == null) return
    // Il nodo è lo stesso oggetto che vive nell'array interno della
    // simulazione d3-force (mai clonato all'ingresso): mutare fx/fy qui è
    // come lo fa d3 stesso durante il drag; setSimNodes sotto pubblica lo
    // snapshot a React.
    /* eslint-disable react-hooks/immutability */
    node.fx = drag.startNodeX + dx
    node.fy = drag.startNodeY + dy
    /* eslint-enable react-hooks/immutability */
    setSimNodes({ ...simNodes })
  }

  function nodePointerUp(
    e: ReactPointerEvent<HTMLDivElement>,
    nodeId: string,
    isExternal: boolean,
  ) {
    const drag = dragState.current
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* no-op */
    }
    const sim = simRef.current
    const node = simNodes[nodeId]
    if (node != null) {
      // v. nodePointerMove
      /* eslint-disable react-hooks/immutability */
      node.fx = null
      node.fy = null
      /* eslint-enable react-hooks/immutability */
    }
    if (sim != null) {
      sim.alphaTarget(0)
      // A fuoco alphaMin è più alto: ripartire da 1 per dare ai nodi lontani
      // i tick necessari ad arrivare a casa; a riposo 0.5 basta.
      if (!reducedMotion) sim.alpha(focus != null ? 1 : 0.5).restart()
    }
    setSimNodes({ ...simNodes })
    dragState.current = null
    if (drag != null && !drag.moved) {
      if (isExternal) {
        const externalNode = graph.externalNodes.find((e2) => e2.nodeId === nodeId)
        onGoToExternal({ chapterId: externalNode?.chapterId ?? '', nodeId })
      } else {
        onFocusChange(focusNodeId === nodeId ? null : nodeId)
      }
    }
  }

  // A riposo: edgesForFamily(graph, family) (con 'all' è già selectRestingEdges).
  // A fuoco: edgesForFamily(...) ∪ hotEdgeIds — gli archi caldi si mostrano
  // tutti, anche nascosti a riposo e fuori dalla famiglia filtrata.
  // Verbo al 60% del segmento dal lato del fuoco (meno collisioni tra archi convergenti).
  // p1 = sorgente, p2 = target: se il fuoco è la sorgente t=0.6, altrimenti 0.4.
  // A fuoco solo gli archi caldi: i residenti non collegati non sono disegnati.
  // A fuoco: archi caldi in evidenza + archi a riposo sfumati sul fondo.
  const visibleEdgeIds = useMemo(
    () => (focus == null ? filteredEdgeIds : new Set([...filteredEdgeIds, ...focus.hotEdgeIds])),
    [focus, filteredEdgeIds],
  )

  // Filtro per famiglia (senza fuoco): i concetti senza relazioni in quella
  // famiglia sfumano. `null` = nessun filtro attivo (family === 'all').
  const familyNodeIds = useMemo(() => {
    if (family === 'all') return null
    const ids = new Set<string>()
    for (const edge of graph.edges) {
      if (!filteredEdgeIds.has(edge.edgeId)) continue
      ids.add(edge.sourceNodeId)
      ids.add(edge.targetNodeId)
    }
    return ids
  }, [graph, family, filteredEdgeIds])

  const focusSimNode = focus != null ? simNodes[focus.focusNodeId] : undefined
  const nodeSummary = focusNodeId != null ? graph.nodes.find((n) => n.nodeId === focusNodeId) : null
  const summaryText = nodeSummary != null ? nodeSummary.summary || nodeSummary.label : ''

  const canvasDragState = useRef<{
    startX: number
    startY: number
    panStartX: number
    panStartY: number
    moved: boolean
  } | null>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const pinchState = useRef<{ dist: number; scale: number } | null>(null)
  const suppressClick = useRef(false)

  function isBackground(e: { target: EventTarget; currentTarget: EventTarget }) {
    const t = e.target as HTMLElement
    return t === e.currentTarget || t.classList?.contains('cm-canvas') === true
  }

  // Zoom ancorato a un punto dello schermo (puntatore o centro delle dita):
  // il punto del layout sotto l'ancora resta fermo mentre la scala cambia.
  function zoomAt(nextScale: number, clientX: number, clientY: number) {
    const el = containerRef.current
    if (el == null) return
    setViewAnimated(false)
    const rect = el.getBoundingClientRect()
    const px = clientX - rect.left
    const py = clientY - rect.top
    const view = viewRef.current
    const next = clampScale(nextScale)
    setPan((prev) => {
      const wx = (px - view.offsetX - prev.x) / view.scale
      const wy = (py - view.offsetY - prev.y) / view.scale
      return { x: px - view.offsetX - wx * next, y: py - view.offsetY - wy * next }
    })
    setUserScale(next)
  }

  function fitView() {
    setViewAnimated(true)
    setUserScale(null)
    const view = viewRef.current
    const fs = focus != null ? simNodesRef.current[focus.focusNodeId] : undefined
    if (fs != null) {
      setPan({
        x: view.width / 2 - (view.offsetX + fs.x * fit.scale),
        y: view.height / 2 - (view.offsetY + fs.y * fit.scale),
      })
    } else {
      setPan({ x: 0, y: 0 })
    }
  }

  function canvasPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!isBackground(e)) return
    setViewAnimated(false)
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* no-op */
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2) {
      const [p, q] = [...pointers.current.values()]
      pinchState.current = { dist: Math.hypot(p!.x - q!.x, p!.y - q!.y), scale }
      canvasDragState.current = null
      return
    }
    canvasDragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      panStartX: pan.x,
      panStartY: pan.y,
      moved: false,
    }
  }

  function canvasPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    const pinch = pinchState.current
    if (pinch != null && pointers.current.size === 2) {
      const [p, q] = [...pointers.current.values()]
      const dist = Math.hypot(p!.x - q!.x, p!.y - q!.y)
      if (pinch.dist > 0) {
        zoomAt(pinch.scale * (dist / pinch.dist), (p!.x + q!.x) / 2, (p!.y + q!.y) / 2)
      }
      suppressClick.current = true
      return
    }
    const drag = canvasDragState.current
    if (drag == null) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) drag.moved = true
    setPan({ x: drag.panStartX + dx, y: drag.panStartY + dy })
  }

  function canvasPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* no-op */
    }
    pointers.current.delete(e.pointerId)
    if (canvasDragState.current?.moved === true) suppressClick.current = true
    if (pointers.current.size < 2) pinchState.current = null
    canvasDragState.current = null
  }

  const zoomAtRef = useRef(zoomAt)
  useEffect(() => {
    zoomAtRef.current = zoomAt
  })

  // Zoom con rotella + Ctrl/Meta a ogni larghezza. Listener nativo non passivo
  // (React registra `wheel` come passivo: preventDefault sarebbe ignorato).
  useEffect(() => {
    const el = containerRef.current
    if (el == null) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      // Passo moltiplicativo: stessa sensazione a ogni scala, più deciso del lineare.
      zoomAtRef.current(viewRef.current.scale * Math.exp(-e.deltaY * 0.0025), e.clientX, e.clientY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  return (
    <div
      className="cm-root"
      ref={containerRef}
      data-testid="chapter-map"
      aria-label={`Mappa del capitolo: ${chapterLabel}`}
      onClick={(e) => {
        if (!isBackground(e)) return
        if (suppressClick.current) {
          suppressClick.current = false
          return
        }
        onFocusChange(null)
      }}
      onDoubleClick={(e) => {
        if (isBackground(e)) fitView()
      }}
      onPointerDown={canvasPointerDown}
      onPointerMove={canvasPointerMove}
      onPointerUp={canvasPointerUp}
      onPointerCancel={canvasPointerUp}
    >
      <div className="cm-chips cm-chips--family" role="group" aria-label="Filtra per famiglia">
        <button
          type="button"
          className={`cm-chip${family === 'all' ? ' cm-chip--active' : ''}`}
          onClick={() => onFamilyChange('all')}
        >
          {FAMILY_LABEL[locale].all}
        </button>
        {FAMILIES.map((f) => (
          <button
            key={f}
            type="button"
            className={`cm-chip cm-chip--${f}${family === f ? ' cm-chip--active' : ''}`}
            onClick={() => onFamilyChange(f)}
          >
            {FAMILY_LABEL[locale][f]}
          </button>
        ))}
      </div>

      <button
        type="button"
        className={`cm-recall${recall ? ' cm-recall--active' : ''}`}
        onClick={() => onRecallChange(!recall)}
        aria-pressed={recall}
        data-testid="chapter-map-recall"
      >
        {copy.recall}
      </button>

      <div
        className={`cm-canvas${viewAnimated ? ' cm-canvas--animated' : ''}`}
        style={{
          transform: `translate(${fit.offsetX + pan.x}px, ${fit.offsetY + pan.y}px) scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <svg className="cm-edges" aria-hidden="true" width={layout.width} height={layout.height}>
          <defs>
            <marker
              id="cm-arrow"
              markerWidth="12"
              markerHeight="12"
              refX="9"
              refY="5"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill="context-stroke" />
            </marker>
          </defs>
          {(() => {
            const pairKey = (x: string, y: string) => (x < y ? `${x}|${y}` : `${y}|${x}`)
            const drawn = graph.edges.filter((edge) => {
              if (relationFamily(edge.relationType) == null) return false
              if (edge.scope === 'intra') return visibleEdgeIds.has(edge.edgeId)
              // Archi cross: mai a riposo, solo quando caldi (e l'esterno è
              // entrato in simNodes — altrimenti a/b sotto sono null).
              return focus?.hotEdgeIds.has(edge.edgeId) === true
            })
            // 1) Geometria di ogni arco (retta, o curva se parallelo a un altro).
            const geoms = drawn.flatMap((edge) => {
              const fam = relationFamily(edge.relationType)
              if (fam == null) return []
              const a = simNodes[edge.sourceNodeId] ?? layoutBox(layout, edge.sourceNodeId)
              const b = simNodes[edge.targetNodeId] ?? layoutBox(layout, edge.targetNodeId)
              if (a == null || b == null) return []
              const hot = focus?.hotEdgeIds.has(edge.edgeId) === true
              const dim = focus != null && !hot
              const dash = fam === 'dynamic' ? '9 6' : fam === 'contrast' ? '2 5' : undefined
              const strokeWidth = hot ? 2.75 : fam === 'contrast' ? 2.25 : 1.75
              // Estremi ritagliati sui bordi dei rettangoli, non ai centri. A fuoco,
              // l'estremo sul fuoco è la "porta" del vicino (angolo del suo posto
              // sull'anello): i raggi si distribuiscono sul perimetro del fuoco.
              const p1 = clipToBox(a, b, a)
              const p2 = clipToBox(b, a, b)
              // Archi paralleli sulla stessa coppia: curve separate, così si
              // leggono tipo e verso di ciascuno invece di sovrapporsi.
              const key = pairKey(edge.sourceNodeId, edge.targetNodeId)
              const siblings = drawn.filter((e) => pairKey(e.sourceNodeId, e.targetNodeId) === key)
              const idx = siblings.findIndex((e) => e.edgeId === edge.edgeId)
              const spreadIdx = siblings.length > 1 ? idx - (siblings.length - 1) / 2 : 0
              const offset = spreadIdx * 26
              const mx = (p1.x + p2.x) / 2
              const my = (p1.y + p2.y) / 2
              const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
              const nx = -(p2.y - p1.y) / len
              const ny = (p2.x - p1.x) / len
              // Controllo della quadratica a 2·offset: la curva passa a ~offset dalla retta.
              const c = { x: mx + nx * offset * 2, y: my + ny * offset * 2 }
              const d =
                offset === 0
                  ? `M${p1.x} ${p1.y} L${p2.x} ${p2.y}`
                  : `M${p1.x} ${p1.y} Q${c.x} ${c.y} ${p2.x} ${p2.y}`
              const at = (t: number) => ({
                x: (1 - t) * (1 - t) * p1.x + 2 * (1 - t) * t * c.x + t * t * p2.x,
                y: (1 - t) * (1 - t) * p1.y + 2 * (1 - t) * t * c.y + t * t * p2.y,
              })
              // Pillola del verbo al 62% dal FUOCO verso l'altro nodo (il verso lo
              // dà la punta grande); gli archi paralleli sfalsano la posizione.
              const focusIsSource = edge.sourceNodeId === focus?.focusNodeId
              const t0 = focusIsSource ? 0.62 + spreadIdx * 0.14 : 0.38 - spreadIdx * 0.14
              const verb = relationVerb(edge.relationType, locale)
              const pw = verb.length * 7.4 + 18
              return [{ edge, fam, hot, dim, dash, strokeWidth, d, at, t0, verb, pw }]
            })
            // 2) Anti-collisione delle pillole: ogni pillola scorre lungo il suo
            //    arco finché non urta né un'altra pillola né un nodo in primo piano.
            type Box = { x: number; y: number; w: number; h: number }
            const overlaps = (r: Box, q: Box, m: number) =>
              !(
                r.x + r.w + m < q.x ||
                q.x + q.w + m < r.x ||
                r.y + r.h + m < q.y ||
                q.y + q.h + m < r.y
              )
            const placed: Box[] = []
            const pills = geoms.map((g) => {
              if (!g.hot) return null
              let first: Box | null = null
              for (const dt of [0, 0.08, -0.08, 0.16, -0.16, 0.24, -0.24]) {
                const t = Math.min(0.9, Math.max(0.1, g.t0 + dt))
                const pt = g.at(t)
                const r: Box = { x: pt.x - g.pw / 2, y: pt.y - 12, w: g.pw, h: 24 }
                first ??= r
                const bad = placed.some((q) => overlaps(r, q, 6))
                if (!bad) {
                  placed.push(r)
                  return r
                }
              }
              if (first != null) placed.push(first)
              return first
            })
            // 3) Disegno: prima tutti gli archi, poi le pillole sopra.
            return (
              <>
                {geoms.map((g) => (
                  <g key={g.edge.edgeId} style={{ opacity: g.dim ? 0.18 : 1 }}>
                    <path
                      data-scope={g.edge.scope}
                      d={g.d}
                      fill="none"
                      stroke={g.hot ? `var(--rel-${g.fam})` : 'var(--line-2)'}
                      strokeWidth={g.strokeWidth}
                      strokeDasharray={g.dash}
                      strokeLinecap="round"
                      markerEnd={g.edge.relationType !== 'opposes' ? 'url(#cm-arrow)' : undefined}
                    />
                  </g>
                ))}
                {geoms.map((g, i) => {
                  const r = pills[i]
                  if (!g.hot || r == null) return null
                  return (
                    <g key={`pill-${g.edge.edgeId}`}>
                      <rect
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={r.h}
                        rx={12}
                        className={`cm-edge-pill cm-edge-pill--${g.fam}`}
                      />
                      <text
                        x={r.x + r.w / 2}
                        y={r.y + r.h / 2}
                        dy="0.35em"
                        className={`cm-edge-verb cm-edge-verb--${g.fam}`}
                        textAnchor="middle"
                      >
                        {g.verb}
                      </text>
                    </g>
                  )
                })}
              </>
            )
          })()}
        </svg>

        <div className="cm-nodes">
          {Object.values(simNodes).map((n) => {
            const graphNode = graph.nodes.find((gn) => gn.nodeId === n.id)
            const externalNode =
              graphNode == null ? graph.externalNodes.find((e) => e.nodeId === n.id) : null
            const label = graphNode?.label ?? externalNode?.label ?? n.id
            const isFocus = n.id === focusNodeId
            const dim =
              focus != null
                ? !isFocus && !focus.neighborIds.has(n.id) && n.ext !== true
                : familyNodeIds != null && n.ext !== true && !familyNodeIds.has(n.id)
            const badgeCount = graphNode != null ? badges[n.id] : undefined
            const relCount = graph.edges.filter(
              (e) => e.sourceNodeId === n.id || e.targetNodeId === n.id,
            ).length
            return (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                aria-label={`${label}, ${relCount} relazioni`}
                className={`cm-node${n.ext === true ? ' cm-node--external' : ''}${isFocus ? ' cm-node--focus' : ''}${n.leaving === true ? ' cm-node--leaving' : ''}`}
                style={{
                  zIndex: isFocus ? 3 : n.bg === true ? 0 : 2,
                  transform: `translate(${n.x - n.w / 2}px, ${n.y - n.h / 2}px)`,
                  width: n.w,
                  minHeight: Math.max(44, n.h),
                  opacity: dim ? 0.35 : 1,
                }}
                data-node-id={n.id}
                onPointerDown={(e) => nodePointerDown(e, n.id)}
                onPointerMove={nodePointerMove}
                onPointerUp={(e) => nodePointerUp(e, n.id, n.ext === true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (n.ext === true) {
                      onGoToExternal({ chapterId: externalNode?.chapterId ?? '', nodeId: n.id })
                    } else {
                      onFocusChange(focusNodeId === n.id ? null : n.id)
                    }
                  }
                }}
              >
                {n.ext === true && externalNode != null && (
                  <small className="cm-node__chapter">
                    {externalLabel(externalNode.chapterId, chapterLabels)}
                  </small>
                )}
                <span className="cm-node__label">{recall && !isFocus ? '···' : label}</span>
                {badgeCount != null && badgeCount > 0 && n.ext !== true && (
                  <span className="cm-node__badge">+{badgeCount}</span>
                )}
              </div>
            )
          })}
          {focusSimNode != null && (
            <div
              className="cm-focus-hint"
              data-testid="chapter-map-focus-hint"
              style={{
                transform: `translate(${focusSimNode.x}px, ${focusSimNode.y + focusSimNode.h / 2 + 22}px) translateX(-50%)`,
              }}
            >
              {copy.backHint}
            </div>
          )}
        </div>
      </div>

      {focus == null && (
        <div className="cm-legend" aria-hidden="true">
          <span className="cm-legend__item">
            <span className="cm-legend__line cm-legend__line--structure" />{' '}
            {FAMILY_LABEL[locale].structure}
          </span>
          <span className="cm-legend__item">
            <span className="cm-legend__line cm-legend__line--dynamic" />{' '}
            {FAMILY_LABEL[locale].dynamic}
          </span>
          <span className="cm-legend__item">
            <span className="cm-legend__line cm-legend__line--contrast" />{' '}
            {FAMILY_LABEL[locale].contrast}
          </span>
        </div>
      )}

      {focus != null && (
        <div className="cm-focusbar" data-testid="chapter-map-focusbar">
          <p className="cm-focusbar__summary">{summaryText}</p>
          <div className="cm-focusbar__actions">
            <button
              type="button"
              onClick={() => onOpenPanel(focus.focusNodeId)}
              className="cm-focusbar__btn cm-focusbar__btn--accent"
            >
              {copy.allRelations}
            </button>
            {onSpeak != null && (
              <button
                type="button"
                onClick={() => onSpeak(summaryText)}
                className="cm-focusbar__btn"
              >
                {copy.listen}
              </button>
            )}
            {!readOnly && nodeSummary != null && nodeSummary.sourceBlockIds.length > 0 && (
              <button
                type="button"
                onClick={() => onGoToManual(nodeSummary.sourceBlockIds[0]!)}
                className="cm-focusbar__btn"
              >
                {copy.goToManual}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function clampScale(v: number): number {
  return Math.min(1.5, Math.max(0.6, v))
}

function initSimNodes(
  layout: ReturnType<typeof layoutChapter>,
  graph: ChapterGraph,
): Record<string, SimNode> {
  const nodes: Record<string, SimNode> = {}
  for (const node of graph.nodes) {
    const box = layout.nodes[node.nodeId]
    if (box == null) continue
    nodes[node.nodeId] = {
      id: node.nodeId,
      x: box.x,
      y: box.y,
      hx: box.x,
      hy: box.y,
      w: box.w,
      h: box.h,
      r: Math.max(box.w, box.h) / 2,
    }
  }
  return nodes
}

function layoutBox(layout: ReturnType<typeof layoutChapter>, nodeId: string) {
  const box = layout.nodes[nodeId]
  if (box == null) return null
  return { x: box.x, y: box.y, w: box.w, h: box.h }
}

/**
 * Ritaglia il punto `from` sul bordo del suo rettangolo, in direzione di
 * `to` (come lo spike "fisica B", funzione `clip`): margine di 4px oltre il
 * bordo così la linea non tocca il contorno del nodo.
 */
function clipToBox(
  from: { x: number; y: number },
  to: { x: number; y: number },
  box: { w: number; h: number },
): { x: number; y: number } {
  const dx = to.x - from.x
  const dy = to.y - from.y
  if (dx === 0 && dy === 0) return { x: from.x, y: from.y }
  const hw = box.w / 2 + 4
  const hh = box.h / 2 + 4
  const tx = dx !== 0 ? hw / Math.abs(dx) : Number.POSITIVE_INFINITY
  const ty = dy !== 0 ? hh / Math.abs(dy) : Number.POSITIVE_INFINITY
  const t = Math.min(tx, ty)
  return { x: from.x + dx * t, y: from.y + dy * t }
}
